<script lang="ts">    
    import { onMount } from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import type { CreateClientNoteRequest, GetClientNoteResponse } from '$lib/models/client/types';
    import { clientStore } from '$lib/stores/clientStore';
    import { XCircle, Warning } from 'phosphor-svelte';
    import { authStore } from '$lib/stores/authStore';
    import { getToastStore, getModalStore } from '@skeletonlabs/skeleton';
    import type { ModalSettings, ModalComponent } from '@skeletonlabs/skeleton';
    import '$lib/utils/date/dateUtility';

    export let clientNotes: GetClientNoteResponse[];
    export let clientId: number;
    export let onRefresh: (() => Promise<void>) | undefined = undefined;
    
    const toastrStore = getToastStore();
    const modalStore = getModalStore();
    let newNoteContent: string = '';
    let isAddingNote: boolean = false;

    let notes: GetClientNoteResponse[] = [];
    $: currentUser = $authStore;

    onMount(async () => {
      notes = clientNotes || [];
    });

    // Update notes when clientNotes prop changes (e.g., after refresh)
    $: if (clientNotes) {
        notes = clientNotes;
    }
    

    async function onNoteAdd(): Promise<void> {
        if (newNoteContent.trim() && !isAddingNote) {
            isAddingNote = true;
            
            try {
                const newNote: CreateClientNoteRequest = {
                    id: 0, // This should be the client ID, not 0
                    noteDescription: newNoteContent.trim(),
                    noteCreator: currentUser?.profile?.name || 'Unknown User',
                    createDate: new Date(),
                    clientId: clientId // Ensure the note is associated with the correct client
                };
                
                // Save to database first
                await clientStore.createClientNote(newNote);
                
                // Refresh from server if callback provided, otherwise update local state
                if (onRefresh) {
                    await onRefresh();
                } else {
                    // Create a GetClientNoteResponse-compatible object for local state
                    const addedNote: GetClientNoteResponse = {
                        id: newNote.id,
                        noteDescription: newNote.noteDescription,
                        noteCreator: newNote.noteCreator,
                        createDate: newNote.createDate || undefined
                    };
                    // Update local state only after successful save
                    clientNotes = clientNotes || [];
                    clientNotes = [...clientNotes, addedNote];
                    notes = clientNotes;
                }
                newNoteContent = '';
                
                toastrStore.trigger({
                    message: 'Note saved successfully.',
                    background: 'bg-success-500',
                    classes: 'text-white'
                });
            } catch (error) {
                console.error('Error saving note:', error);
                toastrStore.trigger({
                    message: `Failed to save note: ${error}`,
                    background: 'bg-error-500',
                    classes: 'text-white'
                });
            } finally {
                isAddingNote = false;
            }
        }
    }

    function onNoteDelete(index: number): void {
        const noteToDelete = notes[index];
        if (!noteToDelete || !currentUser) return;

        const modal: ModalSettings = {
            type: 'confirm',
            title: 'Delete Note',
            body: `
                <div class="flex items-start gap-3">
                    <div class="flex-shrink-0">
                        <div class="w-10 h-10 bg-error-100 rounded-full flex items-center justify-center">
                            <svg class="w-6 h-6 text-error-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                        </div>
                    </div>
                    <div class="flex-1">
                        <p class="text-surface-900 font-medium mb-2">Are you sure you want to delete this note?</p>
                        <p class="text-surface-700 text-sm mb-3">This action cannot be undone. The note will be permanently removed.</p>
                        <div class="bg-surface-100 border-l-4 border-error-500 p-3 rounded">
                            <p class="text-sm text-surface-800">
                                <span class="font-medium">Note content:</span><br>
                                "${noteToDelete.noteDescription?.substring(0, 150)}${noteToDelete.noteDescription && noteToDelete.noteDescription.length > 150 ? '...' : ''}"
                            </p>
                        </div>
                    </div>
                </div>
            `,
            buttonTextCancel: 'Cancel',
            buttonTextConfirm: 'Delete Note',
            modalClasses: '!max-w-md delete-note-modal',
            response: (confirmed: boolean) => {
                if (confirmed) {
                    clientStore.deleteClientNote(clientId, noteToDelete.id, currentUser?.profile?.name || 'Unknown User')
                        .then(async () => {
                            // Refresh from server if callback provided, otherwise update local state
                            if (onRefresh) {
                                await onRefresh();
                            } else {
                                // Remove from local state
                                notes.splice(index, 1);
                                clientNotes = [...notes];
                            }
                            toastrStore.trigger({
                                message: 'Note deleted successfully.',
                                background: 'bg-success-500',
                                classes: 'text-white'
                            });
                        })
                        .catch(error => {
                            console.error('Error deleting note:', error);
                            toastrStore.trigger({
                                message: `Failed to delete note: ${error}`,
                                background: 'bg-error-500',
                                classes: 'text-white'
                            });
                        });
                }
            }
        };

        modalStore.trigger(modal);
    }
</script>

<div class="space-y-4">
    <!-- Add Note Card -->
    <div class="card p-4">
        <h3 class="text-lg font-semibold mb-4 text-primary-500">Add New Note</h3>
        <div class="space-y-4">
            <textarea
                bind:value={newNoteContent}
                placeholder="Enter your note here..."
                class="textarea w-full min-h-[100px] resize-none"
                rows="4"
            ></textarea>
            <div class="flex justify-end">
                <Button
                    label={isAddingNote ? "Saving..." : "Save Note"}
                    colour="primary"
                    size="medium"
                    disabled={!newNoteContent.trim() || isAddingNote}
                    onClick={onNoteAdd}
                />
            </div>
        </div>
    </div>

    <!-- Notes Display Card -->
    <div class="card p-4">
        <h3 class="text-lg font-semibold mb-4 text-primary-500">Notes ({notes.length})</h3>
        {#if notes.length === 0}
            <div class="text-center py-8 text-surface-600">
                <p>No notes have been added yet.</p>
                <p class="text-sm">Add your first note using the form above.</p>
            </div>
        {:else}
            <div class="space-y-3">
                {#each notes as note, index}
                    <div class="border rounded-lg p-4 bg-surface-50">
                        <div class="flex justify-between items-start mb-2">
                            <div class="text-sm text-surface-800">
                                <span class="font-medium text-primary-500">{note.noteCreator || 'Unknown User'}</span>
                                {#if note.createDate}
                                    <span class="ml-2">
                                      {note.createDate ? new Date(note.createDate).toLocalDateTimeFormat('en-AU') : ''}
                                    </span>
                                {/if}
                            </div>
                            {#if note.noteCreator === currentUser?.profile?.name}
                                <button
                                    class="p-1.5 rounded-full bg-error-50 hover:bg-error-100 text-error-600 hover:text-error-700 transition-all duration-200 group"
                                    on:click={() => onNoteDelete(index)}
                                    title="Delete note"
                                >
                                    <XCircle size={18} class="group-hover:scale-110 transition-transform duration-200" />
                                </button>
                            {/if}
                        </div>
                        <div class="font-normal whitespace-pre-wrap">
                            {note.noteDescription}
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
</div>

<style>
    :global(.delete-note-modal .btn-positive),
    :global(.delete-note-modal .btn:last-child) {
        background-color: rgb(239 68 68) !important;
        color: white !important;
        border-color: rgb(239 68 68) !important;
    }
    
    :global(.delete-note-modal .btn-positive:hover),
    :global(.delete-note-modal .btn:last-child:hover) {
        background-color: rgb(220 38 38) !important;
        border-color: rgb(220 38 38) !important;
    }
</style>

